/**
 * ProcessListPage Component
 *
 * List view for production processes (templates and strain-specific).
 * Shows processes grouped by strain and process type.
 *
 * US-PE-002b: Process Management UI Layer
 * US-P2-010: Process Recipe Integration - Process Restructuring
 */

import { useState, useMemo } from 'react'
import ReactDOM from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { PageLayout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import SearchInput from '@/components/ui/search-input'
import {
  useProcessesGroupedByStrain,
  useProcessMutations,
  PROCESS_TYPE_LABELS,
  PROCESS_TYPE_OPTIONS,
  PROCESS_TYPE_ORDER
} from '@/hooks/useProcesses'
import { useAllStrains } from '@/hooks/useStrains'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toast } from 'sonner'
import {
  Workflow,
  ChevronRight,
  ChevronDown,
  Copy,
  Trash2,
  Plus,
  Layers,
  FileStack,
  Dna,
  AlertCircle,
  Beaker,
  Package,
  Sprout,
  Factory
} from 'lucide-react'

/**
 * Icon mapping for process types
 */
const PROCESS_TYPE_ICONS = {
  liquid_culture: Beaker,
  spawn: Sprout,
  block: Factory,
  cultivation: Package
}

/**
 * Template badge styling
 */
function TemplateBadge() {
  return (
    <Badge variant="secondary" className="bg-blue-100 text-blue-700">
      Template
    </Badge>
  )
}

/**
 * Active status indicator
 */
function ActiveStatus({ active }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-sm ${active ? 'text-emerald-600' : 'text-gray-400'}`}>
      <span className={`w-2 h-2 rounded-full ${active ? 'bg-emerald-500' : 'bg-gray-300'}`} />
      {active ? 'Active' : 'Inactive'}
    </span>
  )
}

/**
 * Default phase colors when not set in database
 */
const DEFAULT_PHASE_COLORS = [
  '#8b5cf6', // purple
  '#f59e0b', // amber
  '#10b981', // emerald
  '#3b82f6', // blue
  '#ef4444', // red
  '#ec4899', // pink
  '#14b8a6', // teal
]

/**
 * Process Phase Bar - Visual swimlane showing phases end-to-end
 * Each segment is proportional to the phase duration
 */
function ProcessPhaseBar({ phases, className = '' }) {
  if (!phases || phases.length === 0) {
    return (
      <div className={`h-6 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground ${className}`}>
        No phases defined
      </div>
    )
  }

  const totalDays = phases.reduce((sum, p) => sum + (p.typical_duration_days || 0), 0)

  if (totalDays === 0) {
    return (
      <div className={`h-6 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground ${className}`}>
        No durations set
      </div>
    )
  }

  return (
    <div className={`flex h-6 rounded overflow-hidden border border-border/50 ${className}`} title={`${totalDays} days total`}>
      {phases.map((phase, index) => {
        const duration = phase.typical_duration_days || 0
        const width = (duration / totalDays) * 100
        const color = DEFAULT_PHASE_COLORS[index % DEFAULT_PHASE_COLORS.length]

        // Only show label if segment is wide enough
        const showLabel = width > 12

        return (
          <div
            key={phase.id}
            className="relative flex items-center justify-center text-xs font-medium overflow-hidden transition-all hover:brightness-110"
            style={{
              width: `${width}%`,
              backgroundColor: color,
              color: '#fff',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }}
            title={`${phase.name}: ${duration}d`}
          >
            {showLabel && (
              <span className="truncate px-1">
                {duration}d
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}

/**
 * Compact phase bar for table rows - just colored segments, no text
 */
function ProcessPhaseBarCompact({ phases, className = '' }) {
  if (!phases || phases.length === 0) {
    return <div className={`h-2 bg-muted rounded ${className}`} />
  }

  const totalDays = phases.reduce((sum, p) => sum + (p.typical_duration_days || 0), 0)

  if (totalDays === 0) {
    return <div className={`h-2 bg-muted rounded ${className}`} />
  }

  return (
    <div className={`flex h-2 rounded overflow-hidden ${className}`} title={`${totalDays} days total`}>
      {phases.map((phase, index) => {
        const duration = phase.typical_duration_days || 0
        const width = (duration / totalDays) * 100
        const color = DEFAULT_PHASE_COLORS[index % DEFAULT_PHASE_COLORS.length]

        return (
          <div
            key={phase.id}
            style={{ width: `${width}%`, backgroundColor: color }}
            title={`${phase.name}: ${duration}d`}
          />
        )
      })}
    </div>
  )
}

/**
 * Process type colours for the swimlane — one colour per process type
 */
const SWIMLANE_COLORS = {
  liquid_culture: { bg: '#8b5cf6', text: '#fff' },  // purple
  spawn:          { bg: '#f59e0b', text: '#fff' },  // amber
  block:          { bg: '#10b981', text: '#fff' },  // emerald
  cultivation:    { bg: '#3b82f6', text: '#fff' },  // blue
  untyped:        { bg: '#6b7280', text: '#fff' },  // grey
}

/**
 * Strain Processes Swimlane - Shows ALL phases end-to-end grouped by phase_type.
 * Bracket annotations above show phase-type labels, below show durations.
 */
function StrainProcessesSwimlane({ byType, className = '', onProcessClick }) {
  const [hoveredPhase, setHoveredPhase] = useState(null)
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 })

  // Collect all phases from all process types in order
  const allPhases = []

  PROCESS_TYPE_ORDER.forEach(type => {
    const processes = byType[type] || []
    processes.forEach(process => {
      const phases = process.phases || []
      phases.forEach(phase => {
        allPhases.push({
          ...phase,
          processType: type,
          processId: process.id,
          processName: process.name,
        })
      })
    })
  })

  // Also include untyped processes
  const untyped = byType.untyped || []
  untyped.forEach(process => {
    const phases = process.phases || []
    phases.forEach(phase => {
      allPhases.push({
        ...phase,
        processType: 'untyped',
        processId: process.id,
        processName: process.name,
      })
    })
  })

  // Phases are already in process type order from the collection loop above
  if (allPhases.length === 0) {
    return null
  }

  const totalDays = allPhases.reduce((sum, p) => sum + (p.typical_duration_days || 0), 0)

  if (totalDays === 0) {
    return (
      <div className={`h-10 bg-muted/50 rounded-lg flex items-center justify-center text-sm text-muted-foreground ${className}`}>
        No phase durations defined
      </div>
    )
  }

  // Calculate width percentages per process type for bracket annotations
  const bracketWidths = [...PROCESS_TYPE_ORDER, 'untyped'].map(pt => {
    const days = allPhases
      .filter(p => (p.processType || 'untyped') === pt)
      .reduce((sum, p) => sum + (p.typical_duration_days || 0), 0)
    return {
      type: pt,
      label: PROCESS_TYPE_LABELS[pt] || 'Other',
      days,
      widthPercent: (days / totalDays) * 100,
    }
  }).filter(t => t.days > 0)

  // Track index within each process type group for shade variation
  const processTypeCounters = {}

  const handleMouseEnter = (phase, e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    setPopupPosition({
      x: rect.left + rect.width / 2,
      y: rect.top
    })
    setHoveredPhase(phase)
  }

  const handleMouseLeave = () => {
    setHoveredPhase(null)
  }

  return (
    <div className={`${className} relative`}>
      {/* Top brackets with labels - upward facing */}
      <div className="flex mb-1 overflow-visible">
        {bracketWidths.map(t => (
          <div key={t.type} style={{ width: `${t.widthPercent}%` }} className="px-0.5 overflow-visible relative">
            <div className="absolute left-1/2 -translate-x-1/2 text-xs text-foreground mb-0.5 whitespace-nowrap" title={t.label}>
              {t.label}
            </div>
            <div className="h-4" /> {/* Spacer for the absolute positioned label */}
            <div
              style={{
                borderLeft: '1px solid #000',
                borderRight: '1px solid #000',
                borderTop: '1px solid #000',
                borderRadius: '4px 4px 0 0',
                height: '6px'
              }}
            />
          </div>
        ))}
      </div>

      {/* Main swimlane bar */}
      <div className="flex h-10 rounded-lg overflow-hidden border border-border/50 bg-muted/20">
        {allPhases.map((phase, index) => {
          const duration = phase.typical_duration_days || 0
          if (duration === 0) return null

          const ptKey = phase.processType || 'untyped'
          const typeColors = SWIMLANE_COLORS[ptKey] || SWIMLANE_COLORS.untyped

          // Shade variation within each process type group
          processTypeCounters[ptKey] = (processTypeCounters[ptKey] || 0) + 1
          const shade = 1 - ((processTypeCounters[ptKey] - 1) % 7) * 0.08

          const width = (duration / totalDays) * 100
          const showLabel = width > 8

          return (
            <div
              key={`${phase.id}-${index}`}
              className="relative flex items-center justify-center text-xs font-medium overflow-hidden transition-all hover:brightness-110 hover:scale-y-110 cursor-pointer border-r border-white/20 last:border-r-0"
              style={{
                width: `${width}%`,
                backgroundColor: typeColors.bg,
                opacity: shade,
                color: typeColors.text,
                textShadow: '0 1px 2px rgba(0,0,0,0.3)'
              }}
              onMouseEnter={(e) => handleMouseEnter(phase, e)}
              onMouseLeave={handleMouseLeave}
              onClick={() => onProcessClick?.(phase.processId)}
            >
              {showLabel && (
                <span className="truncate px-1 text-[10px]">
                  {phase.name}
                </span>
              )}
            </div>
          )
        })}
      </div>

      {/* Bottom brackets with durations - downward facing */}
      <div className="flex mt-1">
        {bracketWidths.map(t => (
          <div key={t.type} style={{ width: `${t.widthPercent}%` }} className="px-0.5">
            <div
              style={{
                borderLeft: '1px solid #000',
                borderRight: '1px solid #000',
                borderBottom: '1px solid #000',
                borderRadius: '0 0 4px 4px',
                height: '6px'
              }}
            />
            <div className="text-center text-xs text-muted-foreground mt-0.5">
              {t.days}d
            </div>
          </div>
        ))}
      </div>

      {/* Hover popup - rendered via portal to escape stacking context */}
      {hoveredPhase && ReactDOM.createPortal(
        <div
          className="fixed shadow-lg rounded-lg border border-border px-3 py-2 text-sm pointer-events-none"
          style={{
            left: popupPosition.x,
            top: popupPosition.y - 16,
            transform: 'translate(-50%, -100%)',
            zIndex: 99999,
            backgroundColor: 'white',
            color: '#1f2937'
          }}
        >
          <div className="font-semibold">{hoveredPhase.name}</div>
          <div className="text-xs" style={{ color: '#6b7280' }}>
            {PROCESS_TYPE_LABELS[hoveredPhase.processType] || 'Other'} • {hoveredPhase.typical_duration_days || 0} days
          </div>
        </div>,
        document.body
      )}

      {/* Total summary - moved to bottom right corner */}
      <div className="text-right mt-2 text-xs text-muted-foreground">
        {allPhases.length} phases / {totalDays} days total
      </div>
    </div>
  )
}

/**
 * Process row component for table - simplified for strain tables (Name, Status, Actions)
 */
function ProcessRow({ proc, onRowClick, onClone, onDelete, isTemplate, showStrain = true, simplified = false }) {
  const TypeIcon = proc.process_type ? PROCESS_TYPE_ICONS[proc.process_type] : Workflow
  const totalDays = proc.phases?.reduce((sum, p) => sum + (p.typical_duration_days || 0), 0) || 0

  // Simplified row for strain tables - just Name, Status, Actions
  if (simplified) {
    return (
      <TableRow
        className="cursor-pointer hover:bg-muted/50"
        onClick={() => onRowClick(proc)}
      >
        <TableCell className="font-medium">
          <div className="flex items-center gap-2">
            <TypeIcon className="h-4 w-4 text-muted-foreground" />
            <span>{proc.name}</span>
            {totalDays > 0 && (
              <span className="text-xs text-muted-foreground">({totalDays}d)</span>
            )}
          </div>
        </TableCell>
        <TableCell>
          <ActiveStatus active={proc.active} />
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(proc)}
              className="text-destructive hover:text-destructive"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    )
  }

  // Full row for templates table
  return (
    <TableRow
      className="cursor-pointer hover:bg-muted/50"
      onClick={() => onRowClick(proc)}
    >
      <TableCell className="font-medium">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <TypeIcon className="h-4 w-4 text-muted-foreground" />
            {proc.name}
          </div>
          <ProcessPhaseBarCompact phases={proc.phases} className="w-full max-w-[200px]" />
        </div>
      </TableCell>
      <TableCell className="text-muted-foreground max-w-[200px] truncate">
        {proc.description || '-'}
      </TableCell>
      <TableCell>
        {proc.process_type ? (
          <Badge variant="outline" className="text-xs">
            {PROCESS_TYPE_LABELS[proc.process_type] || proc.process_type}
          </Badge>
        ) : (
          <span className="text-muted-foreground text-sm">-</span>
        )}
      </TableCell>
      <TableCell className="text-center">
        <div className="flex flex-col items-center">
          <Badge variant="outline">{proc.phase_count || 0}</Badge>
          {totalDays > 0 && (
            <span className="text-xs text-muted-foreground mt-0.5">{totalDays}d</span>
          )}
        </div>
      </TableCell>
      {showStrain && (
        <TableCell>
          {proc.strains ? (
            <div className="flex items-center gap-2">
              <Dna className="h-4 w-4 text-muted-foreground" />
              <span>{proc.strains.name}</span>
              <span className="text-xs text-muted-foreground">({proc.strains.code})</span>
            </div>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </TableCell>
      )}
      <TableCell>
        <ActiveStatus active={proc.active} />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {isTemplate && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onClone(proc)}
              title="Clone for strain"
            >
              <Copy className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(proc)}
            className="text-destructive hover:text-destructive"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  )
}

/**
 * Mobile card view for processes
 */
function ProcessCard({ proc, onRowClick, onClone, onDelete, isTemplate }) {
  const TypeIcon = proc.process_type ? PROCESS_TYPE_ICONS[proc.process_type] : Workflow
  const totalDays = proc.phases?.reduce((sum, p) => sum + (p.typical_duration_days || 0), 0) || 0

  return (
    <Card
      className="cursor-pointer hover:bg-muted/50 transition-colors"
      onClick={() => onRowClick(proc)}
    >
      <CardContent className="py-3 px-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <TypeIcon className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{proc.name}</span>
              {isTemplate && <TemplateBadge />}
            </div>
            {proc.description && (
              <p className="text-sm text-muted-foreground line-clamp-2">{proc.description}</p>
            )}
            <div className="flex items-center gap-4 mt-2 text-sm flex-wrap">
              {proc.process_type && (
                <Badge variant="outline" className="text-xs">
                  {PROCESS_TYPE_LABELS[proc.process_type]}
                </Badge>
              )}
              <div className="flex items-center gap-1">
                <Layers className="h-3 w-3 text-muted-foreground" />
                <span>{proc.phase_count || 0} phases</span>
                {totalDays > 0 && (
                  <span className="text-muted-foreground">({totalDays}d)</span>
                )}
              </div>
              {proc.strains && (
                <div className="flex items-center gap-1">
                  <Dna className="h-3 w-3 text-muted-foreground" />
                  <span>{proc.strains.code}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <ActiveStatus active={proc.active} />
            <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
          </div>
        </div>
        {/* Phase timeline bar */}
        <ProcessPhaseBar phases={proc.phases} className="mt-3" />
        <div className="flex items-center gap-2 mt-3 pt-3 border-t" onClick={(e) => e.stopPropagation()}>
          {isTemplate && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onClone(proc)}
            >
              <Copy className="h-3 w-3 mr-1" />
              Clone
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(proc)}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="h-3 w-3 mr-1" />
            Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Strain section with collapsible process types
 */
function StrainSection({ strain, byType, searchTerm, onRowClick, onClone, onDelete }) {
  const [isOpen, setIsOpen] = useState(true)

  // Filter processes by search term
  const filterFn = (p) => {
    if (!searchTerm) return true
    const search = searchTerm.toLowerCase()
    return (
      p.name?.toLowerCase().includes(search) ||
      p.description?.toLowerCase().includes(search) ||
      (p.process_type && PROCESS_TYPE_LABELS[p.process_type]?.toLowerCase().includes(search))
    )
  }

  // Get all processes for this strain that match the filter
  const allProcesses = Object.values(byType).flat().filter(filterFn)
  if (allProcesses.length === 0) return null

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mb-6">
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="w-full justify-between p-4 h-auto hover:bg-muted/50"
        >
          <div className="flex items-center gap-3">
            <Dna className="h-5 w-5 text-purple-600" />
            <span className="text-lg font-semibold">{strain.name}</span>
            <Badge variant="outline" className="text-xs">{strain.code}</Badge>
            <Badge variant="secondary" className="ml-2">{allProcesses.length} processes</Badge>
          </div>
          {isOpen ? (
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          )}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pl-4 space-y-4">
          {/* Full phases swimlane - all phases from all process types */}
          <StrainProcessesSwimlane
            byType={byType}
            className="mx-4 mb-4"
            onProcessClick={(processId) => {
              const process = Object.values(byType).flat().find(p => p.id === processId)
              if (process) onRowClick(process)
            }}
          />

          {/* All processes in a single table - no subsection headers */}
          <div className="ml-4">
            {/* Desktop Table - no headers */}
            <Table className="hidden md:table">
              <TableBody>
                {PROCESS_TYPE_ORDER.flatMap(type => {
                  const processes = (byType[type] || []).filter(filterFn)
                  return processes.map(p => (
                    <ProcessRow
                      key={p.id}
                      proc={p}
                      onRowClick={onRowClick}
                      onClone={onClone}
                      onDelete={onDelete}
                      isTemplate={false}
                      showStrain={false}
                      simplified={true}
                    />
                  ))
                })}
                {/* Untyped processes (legacy) */}
                {(byType.untyped || []).filter(filterFn).map(p => (
                  <ProcessRow
                    key={p.id}
                    proc={p}
                    onRowClick={onRowClick}
                    onClone={onClone}
                    onDelete={onDelete}
                    isTemplate={false}
                    showStrain={false}
                    simplified={true}
                  />
                ))}
              </TableBody>
            </Table>
            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              {PROCESS_TYPE_ORDER.flatMap(type => {
                const processes = (byType[type] || []).filter(filterFn)
                return processes.map(p => (
                  <ProcessCard
                    key={p.id}
                    proc={p}
                    onRowClick={onRowClick}
                    onClone={onClone}
                    onDelete={onDelete}
                    isTemplate={false}
                  />
                ))
              })}
              {/* Untyped processes (legacy) */}
              {(byType.untyped || []).filter(filterFn).map(p => (
                <ProcessCard
                  key={p.id}
                  proc={p}
                  onRowClick={onRowClick}
                  onClone={onClone}
                  onDelete={onDelete}
                  isTemplate={false}
                />
              ))}
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

export default function ProcessListPage({ user, onLogout }) {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showCloneDialog, setShowCloneDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [selectedProcess, setSelectedProcess] = useState(null)
  const [newProcessName, setNewProcessName] = useState('')
  const [newProcessDescription, setNewProcessDescription] = useState('')
  const [newProcessIsTemplate, setNewProcessIsTemplate] = useState(true)
  const [newProcessType, setNewProcessType] = useState('cultivation')
  const [selectedStrainId, setSelectedStrainId] = useState('')

  const { grouped, templates, strains: groupedStrains, loading, error, refetch } = useProcessesGroupedByStrain()
  const { strains } = useAllStrains({ activeOnly: true })
  const { createProcess, cloneForStrain, deleteProcess, updating } = useProcessMutations()

  // Filter templates by search term
  const filteredTemplates = useMemo(() => {
    if (!searchTerm) return templates

    const search = searchTerm.toLowerCase()
    return templates.filter(p =>
      p.name?.toLowerCase().includes(search) ||
      p.description?.toLowerCase().includes(search) ||
      (p.process_type && PROCESS_TYPE_LABELS[p.process_type]?.toLowerCase().includes(search))
    )
  }, [templates, searchTerm])

  const handleRowClick = (process) => {
    navigate(`/production/process/${process.id}`)
  }

  const handleClone = (process) => {
    setSelectedProcess(process)
    setShowCloneDialog(true)
  }

  const handleDelete = (process) => {
    setSelectedProcess(process)
    setShowDeleteDialog(true)
  }

  const handleCreateProcess = async () => {
    if (!newProcessName.trim()) {
      toast.error('Process name is required')
      return
    }

    const { data, error } = await createProcess({
      name: newProcessName.trim(),
      description: newProcessDescription.trim() || null,
      is_template: newProcessIsTemplate,
      strain_id: newProcessIsTemplate ? null : (selectedStrainId || null),
      process_type: newProcessType,
      active: true
    })

    if (error) {
      toast.error('Failed to create process')
    } else {
      toast.success('Process created successfully')
      setShowCreateDialog(false)
      setNewProcessName('')
      setNewProcessDescription('')
      setNewProcessIsTemplate(true)
      setNewProcessType('cultivation')
      setSelectedStrainId('')
      refetch()
      // Navigate to the new process editor
      if (data?.id) {
        navigate(`/production/process/${data.id}`)
      }
    }
  }

  const handleCloneForStrain = async () => {
    if (!selectedStrainId) {
      toast.error('Please select a strain')
      return
    }

    const { data, error } = await cloneForStrain(selectedProcess.id, selectedStrainId)

    if (error) {
      toast.error('Failed to clone process')
    } else {
      toast.success('Process cloned successfully')
      setShowCloneDialog(false)
      setSelectedProcess(null)
      setSelectedStrainId('')
      refetch()
      // Navigate to the new cloned process
      if (data) {
        navigate(`/production/process/${data}`)
      }
    }
  }

  const handleDeleteProcess = async () => {
    const { error } = await deleteProcess(selectedProcess.id)

    if (error) {
      toast.error('Failed to delete process')
    } else {
      toast.success('Process deleted successfully')
      setShowDeleteDialog(false)
      setSelectedProcess(null)
      refetch()
    }
  }

  if (loading) {
    return (
      <PageLayout
        title="Processes"
        user={user}
        onLogout={onLogout}
      >
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
          <p className="ml-3 text-muted-foreground">Loading processes...</p>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title="Processes"
      subtitle="Manage production process templates and configurations"
      user={user}
      onLogout={onLogout}
      headerActions={
        <div className="flex items-center gap-4">
          <SearchInput
            placeholder="Search processes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-64"
          />
          <Button variant="create" onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Process
          </Button>
        </div>
      }
    >
      <div className="h-full overflow-y-auto">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="strains" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="strains" className="gap-2">
              <Dna className="h-4 w-4" />
              Strain Processes
              <Badge variant="secondary" className="ml-1">
                {Object.values(grouped).reduce((acc, g) => acc + Object.values(g.byType).flat().length, 0)}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="templates" className="gap-2">
              <FileStack className="h-4 w-4" />
              Templates
              <Badge variant="secondary" className="ml-1">{filteredTemplates.length}</Badge>
            </TabsTrigger>
          </TabsList>

          {/* Strain Processes Tab */}
          <TabsContent value="strains">
            {groupedStrains.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <Dna className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">No strain-specific processes</h3>
                  <p className="text-muted-foreground">
                    Create processes for each strain to define their production workflows
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {groupedStrains.map(strain => (
                  <StrainSection
                    key={strain.id}
                    strain={strain}
                    byType={grouped[strain.id]?.byType || {}}
                    searchTerm={searchTerm}
                    onRowClick={handleRowClick}
                    onClone={handleClone}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates">
            {filteredTemplates.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <FileStack className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">No templates found</h3>
                  <p className="text-muted-foreground mb-4">
                    {searchTerm ? 'Try adjusting your search' : 'Create a template to get started'}
                  </p>
                  {!searchTerm && (
                    <Button onClick={() => {
                      setNewProcessIsTemplate(true)
                      setShowCreateDialog(true)
                    }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Template
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Desktop Table */}
                <Table className="hidden md:table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-center">Phases</TableHead>
                      <TableHead>Strain</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTemplates.map(p => (
                      <ProcessRow
                        key={p.id}
                        proc={p}
                        onRowClick={handleRowClick}
                        onClone={handleClone}
                        onDelete={handleDelete}
                        isTemplate={true}
                      />
                    ))}
                  </TableBody>
                </Table>

                {/* Mobile Cards */}
                <div className="md:hidden space-y-3">
                  {filteredTemplates.map(p => (
                    <ProcessCard
                      key={p.id}
                      proc={p}
                      onRowClick={handleRowClick}
                      onClone={handleClone}
                      onDelete={handleDelete}
                      isTemplate={true}
                    />
                  ))}
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Process Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Process</DialogTitle>
            <DialogDescription>
              Create a new production process template or strain-specific configuration.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={newProcessName}
                onChange={(e) => setNewProcessName(e.target.value)}
                placeholder="e.g., Standard Mushroom Production"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={newProcessDescription}
                onChange={(e) => setNewProcessDescription(e.target.value)}
                placeholder="Optional description"
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={newProcessIsTemplate ? 'template' : 'strain'}
                onValueChange={(v) => setNewProcessIsTemplate(v === 'template')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="template">Template (can be cloned)</SelectItem>
                  <SelectItem value="strain">Strain-Specific</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Process Type</Label>
              <Select
                value={newProcessType}
                onValueChange={setNewProcessType}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROCESS_TYPE_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                The type of workflow this process represents
              </p>
            </div>
            {!newProcessIsTemplate && (
              <div className="space-y-2">
                <Label>Strain</Label>
                <Select
                  value={selectedStrainId}
                  onValueChange={setSelectedStrainId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a strain" />
                  </SelectTrigger>
                  <SelectContent>
                    {strains.map(strain => (
                      <SelectItem key={strain.id} value={strain.id}>
                        {strain.name} ({strain.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateProcess} disabled={updating}>
              {updating ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clone Process Dialog */}
      <Dialog open={showCloneDialog} onOpenChange={setShowCloneDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clone Process for Strain</DialogTitle>
            <DialogDescription>
              Clone "{selectedProcess?.name}" for a specific strain. This will create a copy of all phases, events, and tasks.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Strain</Label>
              <Select
                value={selectedStrainId}
                onValueChange={setSelectedStrainId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a strain" />
                </SelectTrigger>
                <SelectContent>
                  {strains.map(strain => (
                    <SelectItem key={strain.id} value={strain.id}>
                      {strain.name} ({strain.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCloneDialog(false)
              setSelectedStrainId('')
            }}>
              Cancel
            </Button>
            <Button onClick={handleCloneForStrain} disabled={updating || !selectedStrainId}>
              {updating ? 'Cloning...' : 'Clone'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Process</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedProcess?.name}"? This will also delete all associated phases, events, and tasks. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteProcess}
              disabled={updating}
            >
              {updating ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  )
}
